/**
 * Client-side preview of the CPE 2.3 string the SBOM export builds for a card.
 *
 * Mirrors `_record_to_component` / `_build_cpe23` / `_cpe_escape` in
 * app/tools/cmdbuild_to_sbom.py: Vendor falls back to Hersteller, Product to
 * Bezeichnung, Part to "a" (catalog cards carry no component type); every
 * other empty field becomes "*". Keep both sides in sync.
 */

type FormValues = Record<string, string | boolean | undefined>;

const RESERVED = new Set('!"#$%&\'()*+,/:;<=>?@[\\]^`{|}~');

function text(value: string | boolean | undefined): string {
  return typeof value === 'string' ? value : '';
}

/** NIST IR 7695 escaping as the exporter applies it: spaces → "_", reserved → \xhh. */
function escape(token: string): string {
  let out = '';
  for (const ch of token) {
    const code = ch.codePointAt(0) ?? 0;
    if (ch === ' ') out += '_';
    else if (code >= 0x21 && code <= 0x7e && !RESERVED.has(ch)) out += ch;
    else out += `\\x${code.toString(16).padStart(2, '0')}`;
  }
  return out;
}

function attribute(value: string): string {
  return escape(value).toLowerCase() || '*';
}

/** `null` when neither Vendor/Hersteller nor Product/Bezeichnung yield a value. */
export function cpePreview(values: FormValues): string | null {
  const vendor = text(values.Vendor) || text(values.Hersteller);
  const product = text(values.Product) || text(values.Bezeichnung);
  if (!vendor || !product) return null;
  const part = text(values.Part).trim().toLowerCase();
  const optional = (name: string): string => attribute(text(values[name]).trim());
  return [
    'cpe:2.3',
    ['a', 'o', 'h'].includes(part) ? part : 'a',
    attribute(vendor),
    attribute(product),
    optional('Version'),
    ...['Update', 'Edition', 'Language', 'SwEdition', 'TargetSw', 'TargetHw', 'Other'].map(optional),
  ].join(':');
}

/** Only classes that carry the CPE attribute set get a preview. */
export function hasCpeFields(attributeNames: readonly string[]): boolean {
  return ['Part', 'Vendor', 'Product'].every((name) => attributeNames.includes(name));
}
